import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "What should you do after completing the environmental assessment?",
    options: [
      "Use results to create action plans",
      "Compare scores to others",
      "Focus only on low scores"
    ],
    correctAnswer: 0,
    explanation: "Use the results to identify areas for improvement and create targeted action plans. The assessment is most valuable when used as a tool for personal growth and environmental enhancement."
  },
  {
    question: "What does the WHOQOL-BREF Environmental Domain measure?",
    options: [
      "Quality of your physical and social environment",
      "Your physical health status",
      "Your financial situation"
    ],
    correctAnswer: 0,
    explanation: "The WHOQOL-BREF Environmental Domain assesses the quality of your physical and social environment, including aspects like safety, comfort, and access to resources."
  },
  {
    question: "How should you interpret environmental assessment scores?",
    options: [
      "As indicators of areas for potential improvement",
      "As permanent judgments of your environment",
      "As comparisons with others"
    ],
    correctAnswer: 0,
    explanation: "Assessment scores should be viewed as indicators of areas where you might want to make improvements, not as permanent judgments or competitive comparisons."
  },
  {
    question: "Which environmental factor has the greatest impact on daily well-being?",
    options: [
      "The overall quality and safety of your living environment",
      "The brand of your furniture",
      "The color of your walls"
    ],
    correctAnswer: 0,
    explanation: "The overall quality and safety of your living environment has the greatest impact on daily well-being, affecting everything from stress levels to sleep quality."
  },
  {
    question: "What is the most effective approach to environmental improvement?",
    options: [
      "Making gradual, sustainable changes over time",
      "Making all changes at once",
      "Focusing only on expensive renovations"
    ],
    correctAnswer: 0,
    explanation: "Gradual, sustainable changes are most effective because they allow you to assess what works, build habits, and create lasting improvements in your environment."
  }
];

const EnvironmentalModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('environmentalM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                WHOQOL-BREF Environmental assessment
              </li>
              <li>
                Score interpretation and analysis
              </li>
              <li>
                Personal environmental profiling
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your environmental wellness journey by learning to design supportive environments!</p>
            <p>Ready to discover environmental psychology and create restorative spaces?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/environmental-resources')}
          >
            Return to Environmental Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left environmental-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Environmental Impact Assessment</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about assessing environmental impact.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/environmental-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule3QuizPage;

